import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { Trash2 } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';

const AUTOSAVE_DELAY = 800;

/** One question: the answer saves as it is typed; closing it or reopening it stays a choice. */
const QuestionRow = ({ question, onChanged }) => {
    const [answer, setAnswer] = useState(question.answer || '');
    const [saving, setSaving] = useState(false);
    const [draftStatus, setDraftStatus] = useState('');
    const savedAnswer = useRef(question.answer || '');
    const latestAnswer = useRef(answer);
    latestAnswer.current = answer;

    const saveDraft = async (value) => {
        if (value === savedAnswer.current) return;
        const previous = savedAnswer.current;
        savedAnswer.current = value;
        setDraftStatus('saving');
        try {
            await axiosInstance.patch(`questions/${question.id}/`, { answer: value });
            setDraftStatus('saved');
        } catch (error) {
            console.error('Failed to save answer:', error);
            savedAnswer.current = previous;
            setDraftStatus('error');
        }
    };

    useEffect(() => {
        if (!question.is_open) return undefined;
        const timer = setTimeout(() => saveDraft(answer), AUTOSAVE_DELAY);
        return () => clearTimeout(timer);
    }, [answer]);

    // Leaving the page before the delay is up must not lose what was typed.
    useEffect(() => () => {
        if (question.is_open) saveDraft(latestAnswer.current);
    }, []);

    const update = async (patch) => {
        setSaving(true);
        try {
            await axiosInstance.patch(`questions/${question.id}/`, patch);
            onChanged();
        } catch (error) {
            console.error('Failed to update question:', error);
            alert('Enregistrement impossible.');
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!confirm('Supprimer cette question ?')) return;
        try {
            await axiosInstance.delete(`questions/${question.id}/`);
            onChanged();
        } catch (error) {
            console.error('Failed to delete question:', error);
        }
    };

    const close = (e) => {
        e.preventDefault();
        savedAnswer.current = answer;
        update({ answer, is_open: false });
    };

    return (
        <li className="grid grid-cols-[minmax(0,1fr)] gap-x-4 gap-y-2 py-2.5 border-t border-border md:grid-cols-2 md:items-center">
            <p className="text-sm text-foreground whitespace-pre-line">{question.text}</p>
            <div className="flex items-center gap-2">
                {question.is_open ? (
                    <form onSubmit={close} className="flex flex-1 min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <input
                            aria-label={`Réponse : ${question.text}`}
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            onBlur={() => saveDraft(answer)}
                            placeholder="Réponse de la cuisine…"
                            className="flex-1 min-w-0 px-2 py-1.5 border border-border bg-background text-sm"
                        />
                        <button type="submit" disabled={saving} className="btn-outline shrink-0">
                            Traitée
                        </button>
                        <p aria-live="polite" className={`w-full text-xs empty:hidden ${draftStatus === 'error' ? 'text-bad' : 'text-muted-foreground'}`}>
                            {draftStatus === 'saved' && 'Réponse enregistrée. Touchez « Traitée » pour fermer la question.'}
                            {draftStatus === 'saving' && 'Enregistrement…'}
                            {draftStatus === 'error' && 'Réponse non enregistrée. Vérifiez la connexion et réessayez.'}
                        </p>
                    </form>
                ) : (
                    <>
                        <p className="flex-1 min-w-0 text-sm text-muted-foreground whitespace-pre-line">{question.answer || 'Traitée, sans réponse notée.'}</p>
                        <button type="button" disabled={saving} onClick={() => update({ is_open: true })} className="btn-outline shrink-0">
                            Rouvrir
                        </button>
                    </>
                )}
                <button type="button" aria-label="Supprimer la question" onClick={remove} className="p-1.5 text-bad hover:bg-accent rounded-full shrink-0">
                    <Trash2 className="h-4 w-4" />
                </button>
            </div>
        </li>
    );
};

/** Sorts questions into one group per dish, with the general ones last. */
const groupByDish = (questions) => {
    const groups = new Map();
    questions.forEach((q) => {
        const name = q.dish_name || '';
        groups.set(name, [...(groups.get(name) || []), q]);
    });
    return [...groups.entries()].sort(([a], [b]) => {
        if (!a || !b) return a ? -1 : 1;
        return a.localeCompare(b, 'fr');
    });
};

/** Questions: what still has to be asked to the kitchen, by dish. */
const Questions = () => {
    const [questions, setQuestions] = useState([]);
    const [dishes, setDishes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDone, setShowDone] = useState(false);
    const [text, setText] = useState('');
    const [dishId, setDishId] = useState('');

    const fetchQuestions = async () => {
        try {
            const { data } = await axiosInstance.get('questions/');
            setQuestions(data);
        } catch (error) {
            console.error('Failed to fetch questions:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = 'Questions | Costy';
        fetchQuestions();
        axiosInstance.get('dishes/')
            .then(({ data }) => setDishes([...data].sort((a, b) => a.name.localeCompare(b.name))))
            .catch((error) => console.error('Failed to fetch dishes:', error));
    }, []);

    const add = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        try {
            await axiosInstance.post('questions/', { text: text.trim(), dish: dishId || null });
            setText('');
            fetchQuestions();
        } catch (error) {
            console.error('Failed to add question:', error);
            alert('Enregistrement impossible.');
        }
    };

    const open = questions.filter((q) => q.is_open);
    const done = questions.filter((q) => !q.is_open);
    const shown = showDone ? done : open;

    return (
        <Layout>
            <div className="space-y-6">
                <PageHeader title="Questions" subtitle={`${open.length} à traiter, ${done.length} traitées`}>
                    <button type="button" aria-pressed={showDone} onClick={() => setShowDone((v) => !v)} className="btn-outline">
                        {showDone ? 'Voir à traiter' : 'Voir traitées'}
                    </button>
                </PageHeader>

                <form onSubmit={add} className="panel p-3 flex flex-wrap gap-2">
                    <input
                        aria-label="Nouvelle question"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Nouvelle question…"
                        className="w-full sm:w-auto sm:flex-1 min-w-0 px-3 py-2 border border-border bg-background text-sm"
                    />
                    <select aria-label="Plat concerné" value={dishId} onChange={(e) => setDishId(e.target.value)} className="w-full min-w-0 sm:w-auto sm:max-w-xs px-2 py-2 border border-border bg-background text-sm">
                        <option value="">Général</option>
                        {dishes.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <button type="submit" className="btn-primary">Ajouter</button>
                </form>

                {loading ? (
                    <p className="text-sm text-muted-foreground">Chargement…</p>
                ) : shown.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{showDone ? 'Rien de traité pour l’instant.' : 'Aucune question ouverte.'}</p>
                ) : (
                    <div className="space-y-6">
                        {groupByDish(shown).map(([name, group]) => (
                            <section key={name || 'general'}>
                                <h2 className="pb-2 text-xl text-foreground">
                                    {name || 'Général'} <span className="text-sm font-normal text-muted-foreground">({group.length})</span>
                                </h2>
                                <ul>
                                    {group.map((q) => <QuestionRow key={q.id} question={q} onChanged={fetchQuestions} />)}
                                </ul>
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default Questions;
